/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { strings } from "../../../common/Strings/Strings";
import { useClinic } from "../../../contexts/ClinicContext";
import { SavedReservationProgress } from "../../../models/calendar/SavedReservationProgress";
import { NewReservationPetType } from "../../../models/calendar/NewReservationPetType";
import { saveReservationInProgress } from "../../../util/LocalStorageVariables";
import NewExternalMedicalRecordModal from "../../Pet/History/NewExternalMedicalRecordModal";
import { PetOwnerResponse } from "../../../models/pet/PetOwnerResponse";
import TodoModal from "../../Todo/TodoModal";
import { ClinicPetResponse } from "../../../models/pet/ClinicPetResponse";
import { Feature } from "../../../models/clinic/Feature";
import { ClinicFeature } from "../../../models/clinic/ClinicFeature";
import { getGeneralError } from "../../../util/helperFunctions";
import ClinicPetApi from "../../../api/ClinicPetApi";
import BeforeDeleteModal from "../../../components/Modals/BeforeDeleteModal";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Button from "../../../components/Button";
import { ElipsisVertical } from "../../../common/Icons/ElipsisVertical";
import { PlusIcon } from "../../../common/Icons/PlusIcon";

interface Props {
  pet: ClinicPetResponse;
  owner?: PetOwnerResponse;
  onError: (error: string | null) => void;
  onLoad: (loading: boolean) => void;
}

const PetListRowActions = (props: Props): ReactElement => {
  const { pet, owner, onError, onLoad } = props;
  const [externalMedicalRecordModal, setExternalMedicalRecordModal] = useState<boolean>(false);
  const [newTodoModal, setNewTodoModal] = useState<boolean>(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const { clinic } = useClinic();
  const navigate = useNavigate();

  const openExternalMedicalRecordModal = () => {
    setExternalMedicalRecordModal(true);
  };

  const closeExternalMedicalRecordModal = () => {
    setExternalMedicalRecordModal(false);
  };

  const openNewTodoModal = () => {
    setNewTodoModal(true);
  };

  const closeNewTodoModal = () => {
    setNewTodoModal(false);
  };

  const createNewAppointment = () => {
    if (pet && owner) {
      const temp: SavedReservationProgress = {
        petState: {
          state: NewReservationPetType.SELECTED,
          pet: {
            clinicPetDetails: {
              id: pet.clinicPetDetails.id,
              name: pet.clinicPetDetails.name,
              gender: pet.clinicPetDetails.gender,
              species: pet.clinicPetDetails.species,
              breed: pet.clinicPetDetails.breed,
              chronicDiseases: pet.clinicPetDetails.chronicDiseases || [],
              deceased: pet.clinicPetDetails.deceased,
            },
          },
          petOwner: owner,
        },
        symptoms: [],
      };

      saveReservationInProgress(temp);
      navigate("/");
    }
  };

  const deletePet = async () => {
    try {
      onLoad(true);
      onError(null);
      setDeleteModalVisible(false);
      await ClinicPetApi.archiveClinicPet(pet.clinicPetDetails.id);
      navigate("/pet-owner-profile/" + owner?.petOwnerDetails?.id, { replace: true });
    } catch (e) {
      onError(await getGeneralError(e));
    } finally {
      onLoad(false);
    }
  };

  return (
    <div className="flex space-x-3">
      <Dropdown className="min-w-48">
        <Dropdown.Toggle>
          <Button variant="icon">
            <PlusIcon />
          </Button>
        </Dropdown.Toggle>
        <Dropdown.Content>
          {clinic?.availableFeatures?.includes(ClinicFeature.PRESCRIPTION) ? (
            <li>
              <Link className="dropdown-item" to={`/prescription/${pet.clinicPetDetails?.id}`}>
                {strings.newPrescription}
              </Link>
            </li>
          ) : (
            <></>
          )}
          <li>
            <Button className="dropdown-item" onClick={createNewAppointment} variant="link">
              {strings.newAppointment}
            </Button>
          </li>
          <li>
            <Button className="dropdown-item" onClick={openNewTodoModal} variant="link">
              {strings.newTask}
            </Button>
          </li>
          <li>
            <Button
              className="dropdown-item"
              onClick={() => {
                openExternalMedicalRecordModal();
              }}
              variant="link"
            >
              {strings.addNewExternalMedicalRecord}
            </Button>
          </li>
        </Dropdown.Content>
      </Dropdown>
      <Dropdown className="min-w-48">
        <Dropdown.Toggle>
          <Button variant="icon">
            <ElipsisVertical />
          </Button>
        </Dropdown.Toggle>
        <Dropdown.Content>
          <li>
            <Link className="dropdown-item" to={`/edit-clinic-pet/${pet.clinicPetDetails.id}`}>
              {strings.editPet}
            </Link>
          </li>
          <li>
            <Button
              className="dropdown-item"
              onClick={() => {
                setDeleteModalVisible(true);
              }}
              variant="link"
            >
              {strings.deleteClinicPet}
            </Button>
          </li>
          <li hidden={!clinic?.features?.includes(Feature.RESERVATION)}>
            <Link className="dropdown-item" to={`/last-medical-record/${pet.clinicPetDetails.id}`}>
              {strings.openLastMedicalRecord}
            </Link>
          </li>
        </Dropdown.Content>
      </Dropdown>
      <NewExternalMedicalRecordModal
        close={closeExternalMedicalRecordModal}
        isOpen={externalMedicalRecordModal}
        petId={pet.clinicPetDetails.id}
      />
      <TodoModal
        onHide={closeNewTodoModal}
        show={newTodoModal}
        todoForPet={owner ? { pet, petOwner: owner } : undefined}
      />
      <BeforeDeleteModal
        open={isDeleteModalVisible}
        onHide={() => setDeleteModalVisible(false)}
        returnYes={deletePet}
        content={strings.areYouSureDeletePet}
      />
    </div>
  );
};

export default PetListRowActions;
